import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, StandaloneSearchBox, InfoWindow } from '@react-google-maps/api';
import { Box, Typography, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBC28IfOmHnlcxvQo1sW9sduMvJ9jENTlE';

const defaultCenter = {
  lat: 28.6139,
  lng: 77.2090
};

const GoogleMapComponent = ({
  onLocationSelect,
  selectedLocation,
  hideSearch = false,
  overlaySearch = false,
  showPinHint = false,
  height = '350px'
}) => {
  const [map, setMap] = useState(null);
  const [hintOpen, setHintOpen] = useState(true);
  const searchBoxRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  });

  const onLoad = useCallback(function callback(mapInstance) {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  const handleMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    onLocationSelect(lat, lng);
    setHintOpen(true);
  }, [onLocationSelect]);

  const onSearchBoxLoad = useCallback((ref) => {
    searchBoxRef.current = ref;
  }, []);

  const onPlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      if (map) {
        map.panTo({ lat, lng });
        map.setZoom(16);
      }

      onLocationSelect(lat, lng);
      setHintOpen(true);
    }
  }, [map, onLocationSelect]);

  const searchField = (
    <StandaloneSearchBox
      onLoad={onSearchBoxLoad}
      onPlacesChanged={onPlacesChanged}
    >
      <TextField
        fullWidth
        placeholder="Search for area / locality"
        variant="outlined"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          sx: {
            backgroundColor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ccc',
            },
          },
        }}
      />
    </StandaloneSearchBox>
  );

  if (loadError) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography color="error">
          Error loading Google Maps. Please check your internet connection or API key.
        </Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading Google Maps...
          </Typography>
        </Box>
      </Box>
    );
  }

  const markerPosition = selectedLocation
    ? {
        lat: parseFloat(selectedLocation.lat),
        lng: parseFloat(selectedLocation.lng)
      }
    : null;

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      {!hideSearch && !overlaySearch && (
        <Box sx={{ mb: 2, position: 'relative', zIndex: 1000 }}>
          {searchField}
        </Box>
      )}

      {overlaySearch && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 72,
            zIndex: 2,
          }}
        >
          {searchField}
        </Box>
      )}

      <Box sx={{ height: overlaySearch || hideSearch ? '100%' : undefined, overflow: 'hidden' }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height }}
          center={markerPosition || defaultCenter}
          zoom={markerPosition ? 16 : 11}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            streetViewControl: false,
            fullscreenControl: true,
            mapTypeControl: false,
            zoomControl: true,
          }}
        >
          {markerPosition && (
            <Marker
              position={markerPosition}
              draggable
              onDragEnd={(event) => {
                const lat = event.latLng.lat();
                const lng = event.latLng.lng();
                onLocationSelect(lat, lng);
                setHintOpen(true);
              }}
              onClick={() => setHintOpen(true)}
            />
          )}
          {showPinHint && hintOpen && markerPosition && (
            <InfoWindow
              position={markerPosition}
              onCloseClick={() => setHintOpen(false)}
              options={{
                pixelOffset:
                  typeof window !== 'undefined' && window.google?.maps
                    ? new window.google.maps.Size(0, -36)
                    : undefined
              }}
            >
              <Box sx={{ maxWidth: 220, p: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                  Orders will be delivered here. Place the pin accurately on the map.
                </Typography>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      </Box>
    </Box>
  );
};

export default GoogleMapComponent;
